import { setItemVisual } from './icons';
import * as THREE from 'three';
import { PlayerInventory, type ItemId, ITEMS } from '../game/inventory';
import { DragController } from './drag';
import { buildHumanoid } from '../game/player';
import { SK, btnCss, redHover, injectSkinCss } from './skin';
import { buildShell, splitBody, paneHead, slotEl, type Shell } from './shell';

// The INVENTORY window (TAB / I) on the shared 1240 shell. LAYOUT B split: LEFT pane = the character
// doll box beside a 2-column x 4-row equip grid (7 clothing + a BACKPACK slot); RIGHT pane = an 8-wide
// grid of 24 backpack slots. The belt row was REMOVED (owner 2026-07-13: redundant -- the HUD hotbar at
// the bottom of the screen IS the belt, and it stays a live drop target below the window). Crafting
// lives on its own Q menu.

const DOLL_W = 280, DOLL_H = 360;         // character doll box (shrunk with the belt gone -- shorter window)
const GRID_COLS = 8, GRID_ROWS = 3;       // spec: 8-wide backpack, 24 slots (matches GRID_SIZE)
const SLOT_GAP = 8;                        // --ox-slot-gap
const EQUIP_COLS = 2;                      // equip grid: 2 columns x 4 rows beside the doll
const EQUIP: Array<{ key: string; label: string }> = [
  { key: 'helmet', label: 'Helmet' },
  { key: 'chest', label: 'Chest' },
  { key: 'shirt', label: 'Shirt' },
  { key: 'legs', label: 'Legs' },
  { key: 'pants', label: 'Pants' },
  { key: 'boots', label: 'Boots' },
  { key: 'gloves', label: 'Gloves' },
  { key: 'backpack', label: 'Backpack' }, // owner 2026-07-13: future carry-expansion slot
];

export class LoadoutScreen {
  private readonly shell: Shell;
  private readonly invGrid: HTMLDivElement;
  headerExtra!: HTMLDivElement; // the inventory pane-head; main appends extra buttons (notification bell)
  private readonly canvas: HTMLCanvasElement;
  private open = false;
  onClose: (() => void) | null = null;
  onUseItem: ((id: ItemId, index: number) => void) | null = null; // double-click a stack: eat food / equip to a free hotbar slot

  // Mini 3D character preview (its own tiny renderer; built lazily on first open, error-guarded).
  private renderer: THREE.WebGLRenderer | null = null;
  private pscene: THREE.Scene | null = null;
  private pcam: THREE.PerspectiveCamera | null = null;
  private char: THREE.Group | null = null;
  private raf = 0;
  private inited = false;

  constructor(private readonly inv: PlayerInventory, private readonly drag: DragController) {
    injectSkinCss();
    this.shell = buildShell({
      title: 'Inventory', closeKey: 'TAB', bodyPad: false,
      hintsHtml: `Drag &middot; shift-click to move &middot; ctrl-click half &middot; alt-click one`,
    });
    this.shell.scrim.dataset.drop = 'world'; // dropping a stack onto empty space throws it into the world
    this.shell.win.dataset.drop = 'panel';   // releasing a drag over the window chrome is a no-op (not a world-drop)
    // A plain click / right-click on empty backdrop (NOT the window, NOT the end of a drag-out) closes it.
    const outsideTap = (e: MouseEvent): boolean => e.target === this.shell.scrim && !this.drag.dragging;
    this.shell.scrim.addEventListener('contextmenu', (e) => { e.preventDefault(); if (outsideTap(e)) this.close(); });
    this.shell.scrim.addEventListener('mouseup', (e) => { if (e.button === 0 && outsideTap(e)) this.close(); });

    const { left, right } = splitBody(this.shell);

    // LEFT pane: doll box + the 2x4 equip grid (clothing + backpack), side by side with a 16 gap.
    left.appendChild(paneHead('Character', `${EQUIP.length} equip slots`));
    const dollRow = document.createElement('div');
    dollRow.style.cssText = css('display:flex', 'gap:16px', 'align-items:flex-start');
    this.canvas = document.createElement('canvas');
    this.canvas.width = DOLL_W; this.canvas.height = DOLL_H;
    this.canvas.style.cssText = css(`width:${DOLL_W}px`, `height:${DOLL_H}px`, 'flex:none',
      'background:radial-gradient(ellipse at 50% 30%,rgba(70,64,56,0.35),rgba(0,0,0,0) 62%)',
      `border:1px solid ${SK.border}`, `border-radius:${SK.rMd}`, 'pointer-events:none');
    const equipGrid = document.createElement('div');
    equipGrid.style.cssText = css('display:grid', `grid-template-columns:repeat(${EQUIP_COLS},64px)`, `gap:${SLOT_GAP}px`, 'flex:none');
    for (const c of EQUIP) {
      const slot = slotEl('grid');
      slot.dataset.clothing = c.key; // tagged for a future armor/equip system (no drop wiring yet)
      const ghost = document.createElement('div');
      ghost.textContent = c.label;
      ghost.style.cssText = css('position:absolute', 'inset:0', 'display:grid', 'place-items:center',
        `font:700 9px ${SK.font}`, 'letter-spacing:0.08em', 'text-transform:uppercase', `color:${SK.faint}`, 'pointer-events:none');
      slot.appendChild(ghost);
      equipGrid.appendChild(slot);
    }
    dollRow.append(this.canvas, equipGrid);
    left.appendChild(dollRow);

    // RIGHT pane: inventory pane-head (label + Sort, and main mounts the bell here) + the 8-wide grid.
    const invHead = paneHead('Inventory', '');
    const headActions = document.createElement('div');
    headActions.style.cssText = 'display:flex;align-items:center;gap:10px';
    const sortBtn = document.createElement('button');
    sortBtn.innerHTML = 'Sort';
    sortBtn.style.cssText = btnCss('secondary', true);
    sortBtn.addEventListener('click', () => this.inv.sortMerge());
    redHover(sortBtn);
    headActions.appendChild(sortBtn);
    // Replace the pane-head's meta node with the actions cluster (main appends the bell to headActions).
    (invHead.lastElementChild as HTMLElement).replaceWith(headActions);
    this.headerExtra = headActions;
    right.appendChild(invHead);

    this.invGrid = document.createElement('div');
    this.invGrid.dataset.drop = 'inventory';
    this.invGrid.style.cssText = css('display:grid', `grid-template-columns:repeat(${GRID_COLS},64px)`, `gap:${SLOT_GAP}px`, 'flex:none');
    right.appendChild(this.invGrid);

    document.body.appendChild(this.shell.scrim);
    this.inv.subscribe(() => { if (this.open) this.renderInv(); });
  }

  get isOpen(): boolean { return this.open; }
  toggle(): void { this.open ? this.close() : this.show(); }

  show(): void {
    if (this.open) return;
    this.open = true;
    this.shell.scrim.style.display = 'flex';
    this.renderInv();
    // DEFER the 3D character preview a frame: a second WebGLRenderer + buildHumanoid is ~100ms of sync
    // work that would hitch every open. The grid is interactive immediately; the portrait pops in next.
    requestAnimationFrame(() => {
      if (!this.open) return;
      this.ensureRenderer();
      if (this.renderer && !this.raf) this.raf = requestAnimationFrame((t) => this.tick(t));
    });
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.shell.scrim.style.display = 'none';
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    this.onClose?.();
  }

  private tick(t: number): void {
    if (!this.open) return;
    this.raf = requestAnimationFrame((tt) => this.tick(tt));
    if (this.char) this.char.rotation.y = t * 0.0009;
    if (this.renderer && this.pscene && this.pcam) this.renderer.render(this.pscene, this.pcam);
  }

  private ensureRenderer(): void {
    if (this.inited) return;
    this.inited = true;
    try {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
      this.renderer.setSize(DOLL_W, DOLL_H, false);
      this.pscene = new THREE.Scene();
      this.pscene.add(new THREE.HemisphereLight(0xdfeaff, 0x3a3a44, 1.2));
      const dir = new THREE.DirectionalLight(0xffffff, 1.1); dir.position.set(2, 5, 3); this.pscene.add(dir);
      this.char = buildHumanoid().body;
      this.pscene.add(this.char);
      this.pcam = new THREE.PerspectiveCamera(30, DOLL_W / DOLL_H, 0.1, 100);
      this.pcam.position.set(0, 1.0, 4.2);
      this.pcam.lookAt(0, 0.92, 0);
    } catch { this.renderer = null; } // no WebGL context -> the styled box is the fallback
  }

  private renderInv(): void {
    const grid = this.invGrid;
    grid.replaceChildren();
    for (let i = 0; i < GRID_COLS * GRID_ROWS; i++) {
      const s = this.inv.stackAt(i);
      const cell = s ? this.filledCell(s.id, s.n, i) : slotEl('grid');
      cell.dataset.drop = 'inventory';
      cell.dataset.slot = String(i);
      grid.appendChild(cell);
    }
  }

  // A filled backpack-grid slot: item icon/swatch + name + count, draggable, double-click to use/equip.
  private filledCell(id: ItemId, n: number, index: number): HTMLDivElement {
    const cell = slotEl('grid');
    const iconed = setItemVisual(cell, id, tint(ITEMS[id].color));
    // Dark ink stays hardcoded: it sits on the item's own light tint() swatch, not on a UI surface.
    const inkCss = iconed ? `color:${SK.textHi};text-shadow:0 1px 3px rgba(0,0,0,0.95)` : 'color:#0c0f14';
    cell.style.borderColor = SK.borderStrong;
    cell.style.cursor = 'grab';
    const name = document.createElement('div');
    name.textContent = ITEMS[id].name;
    name.style.cssText = css('position:absolute', 'left:0', 'right:0', 'top:0', 'padding:4px 4px 0', 'font-size:9px', 'line-height:1.1', inkCss, 'font-weight:600', 'pointer-events:none', 'word-break:break-word');
    cell.appendChild(name);
    const cnt = document.createElement('div');
    cnt.textContent = String(n);
    cnt.style.cssText = css('position:absolute', 'right:5px', 'bottom:4px', 'font-size:14px', 'font-weight:700', 'font-variant-numeric:tabular-nums', inkCss, 'pointer-events:none');
    cell.appendChild(cnt);
    cell.addEventListener('mousedown', (e) => {
      if (e.button === 0) this.drag.beginCandidate({ source: 'inventory', id, index, n }, `${ITEMS[id].name} (${n})`, e);
    });
    cell.addEventListener('dblclick', (e) => { if (e.button === 0) { this.drag.cancel(); this.onUseItem?.(id, index); } });
    return cell;
  }
}

// --- styling helpers ---
function css(...lines: string[]): string { return lines.join(';'); }
function tint(hex: number): string {
  const r = (hex >> 16) & 0xff, g = (hex >> 8) & 0xff, b = hex & 0xff;
  const mix = (c: number) => Math.round(c * 0.62 + 255 * 0.38);
  return '#' + (((mix(r) << 16) | (mix(g) << 8) | mix(b)) >>> 0).toString(16).padStart(6, '0');
}
